import { logger, storageHelpers } from '@utils'

import { client } from './ApolloClient'
import { RETRIEVE_EMPLOYEE_OTP, RETRIEVE_EMPLOYEE_ACCESS_TOKEN, RETRIEVE_EMPLOYEE } from './schema'
import ErrorTrackingAPI from '../ErrorTrackingAPI'

/**
 * Employee Services
 * handle employee related GraphQL operations
 */

/**
 * retrieve OTP for employee login
 * @param {string} email - employee email address
 * @returns {Promise<Object>} OTP result
 */
export const retrieveEmployeeOTP = async email => {
    try {
        const { data } = await client.query({
            query: RETRIEVE_EMPLOYEE_OTP,
            variables: { email },
            fetchPolicy: 'no-cache',
        })

        return {
            success: data.retrieveOTP.result,
            error: data.retrieveOTP.error,
            expiryMinutes: data.retrieveOTP.expiryMinutes,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'EmployeeService', 'retrieveEmployeeOTP')
        return {
            success: false,
            error: error.message || 'retrieve employee OTP error',
            expiryMinutes: null,
        }
    }
}

/**
 * retrieve access token for employee after OTP verification
 * @param {string} email - employee email address
 * @param {string} code - OTP code for verification
 * @returns {Promise<Object>} access token result with employee information
 */
export const retrieveEmployeeAccessToken = async (email, code) => {
    try {
        const { data } = await client.query({
            query: RETRIEVE_EMPLOYEE_ACCESS_TOKEN,
            variables: { email, code },
            fetchPolicy: 'no-cache',
        })

        const { result: success, error, accessToken, expiredAt, employee } = data.retrieveAccessToken

        // Store tokens if successful
        if (success && accessToken) {
            storageHelpers.setEmployeeAccessToken(accessToken)
            if (expiredAt) {
                storageHelpers.setEmployeeTokenExpiresAt(expiredAt)
            }
            logger.info('Employee access token stored successfully')
        }

        return {
            success,
            error,
            accessToken,
            expiredAt,
            employee,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'EmployeeService', 'retrieveEmployeeAccessToken')
        return {
            success: false,
            error: error.message || 'retrieve employee access token error',
            accessToken: null,
            expiredAt: null,
            employee: null,
        }
    }
}

/**
 * retrieve employee information using access token
 * @param {string} accessToken - employee access token
 * @returns {Promise<Object>} employee result
 */
export const retrieveEmployee = async accessToken => {
    try {
        const { data } = await client.query({
            query: RETRIEVE_EMPLOYEE,
            variables: { accessToken },
            fetchPolicy: 'no-cache',
            context: {
                headers: {
                    'X-Access-Token': accessToken,
                },
            },
        })

        return {
            success: data.retrieveEmployee.result,
            error: data.retrieveEmployee.error,
            employee: data.retrieveEmployee.employee,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'EmployeeService', 'retrieveEmployee')
        return {
            success: false,
            error: error.message || 'retrieve employee error',
            employee: null,
        }
    }
}

/**
 * Build group name from employee information
 * @param {Object} employee - employee object
 * @returns {string|null} group name in format 'pos-{type}-{id}' or null if cannot build
 */
export const buildGroupNameFromEmployee = employee => {
    if (!employee) return null

    // Priority: cashRegisterId > stallId
    if (employee.cashRegisterId) {
        return `pos-CASH_REGISTER-${employee.cashRegisterId}`
    } else if (employee.stallId) {
        return `pos-STALL-${employee.stallId}`
    }

    return null
}

/**
 * get stored employee tokens
 * @returns {Object} tokens object
 */
export const getEmployeeTokens = () => {
    return {
        accessToken: storageHelpers.getEmployeeAccessToken(),
        expiredAt: storageHelpers.getEmployeeTokenExpiresAt(),
    }
}

/**
 * clear employee tokens
 */
export const clearEmployeeTokens = () => {
    storageHelpers.clearEmployeeAccessToken()
    storageHelpers.clearEmployeeTokenExpiresAt()
    // Clear Sentry user context when employee logs out
    ErrorTrackingAPI.clearUserContext()
    logger.info('Employee tokens cleared')
}

export default {
    retrieveEmployeeOTP,
    retrieveEmployeeAccessToken,
    retrieveEmployee,
    getEmployeeTokens,
    clearEmployeeTokens,
    buildGroupNameFromEmployee,
}
